import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { message } from 'antd';

/**
 * Preview 화면이 어떤 경로로 도달했는지 (테이블 체크박스 vs 엑셀 업로드).
 * execute 시 배치 이력에 같이 저장되어 추후 운영 추적에 사용.
 */
export type EligibleItemsSource = 'tableSelection' | 'excelUpload';

/**
 * `shiftConfig.buildShift()` 의 결과 형태. 도메인별 shift 타입이 다르므로
 * generic `TShift` 로 흡수.
 */
type BuildShiftResult<TShift> = { shift: TShift } | { error: string };

type UseEligibleItemsPreviewArgs<TShift, TRes> = {
  /** 도메인 shift 입력 검증 + 빌드 — 보통 `useShiftConfig.buildShift` 그대로 전달. */
  buildShift: () => BuildShiftResult<TShift>;
  /**
   * 검증을 통과한 ids + shift 로 Preview API 를 호출하는 콜백.
   *
   * 도메인별 PreviewRequest 구조 (`selection.mode` 등) 가 다르므로 호출처가
   * 직접 request 를 빌드하고 mutateAsync 를 호출. 훅은 그 결과 (Promise<TRes>) 만
   * 받아 state 에 반영한다.
   *
   * @example
   * onSubmit: ({ ids, shift }) =>
   *   previewMutation.mutateAsync({ selection: { mode: 'idList', ids }, shift })
   */
  onSubmit: (params: { ids: number[]; shift: TShift }) => Promise<TRes>;
  /**
   * preview 성공 직후 한 번 호출되는 후처리 슬롯.
   * 예: 기준일 변경의 `resetOverrides()` — 새 preview 가 오면 이전 수동 지정값 무효화.
   * 완료일 변경은 override 개념이 없어 undefined.
   */
  onAfterPreviewSuccess?: () => void;
};

type UseEligibleItemsPreviewResult<TRes> = {
  /** Preview 결과 (모달 표시용). null 이면 모달 닫힘. */
  previewResult: TRes | null;
  setPreviewResult: Dispatch<SetStateAction<TRes | null>>;
  /** 마지막 preview 의 선정 경로. execute 시 이력에 함께 저장. */
  currentSource: EligibleItemsSource;
  /**
   * Preview API 호출 흐름 (검증 → onSubmit → state 갱신 → 후처리).
   *
   * `idsOverride` 가 주어지면 인자 ids 를 사용 (엑셀 업로드 경로), 미지정이면
   * 호출처가 관리하는 `selectedIds` 를 그대로 전달해야 함.
   *
   * @returns 성공 여부 — 호출처에서 후속 동기화(예: `setSelectedIds(idsOverride)`) 분기에 사용.
   */
  runPreview: (
    selectedIds: number[],
    idsOverride?: number[],
  ) => Promise<boolean>;
};

/**
 * 정산 기준일 / 완료일 변경의 ExecuteTabPanel 공통 Preview 흐름.
 *
 * 두 도메인 모두 다음 흐름이 동일:
 *  1) idsOverride ?? selectedIds 로 대상 id 확정 (0 건이면 warning)
 *  2) `buildShift()` 결과가 error 면 warning
 *  3) `onSubmit({ ids, shift })` 으로 도메인 PreviewRequest 빌드 + mutation 호출
 *  4) 성공 시 previewResult 저장 + source 기록
 *  5) 도메인별 후처리 (`onAfterPreviewSuccess` 슬롯)
 *
 * 도메인 차이(예: 기준일 변경의 dueDateOverrides reset) 는 슬롯으로 흡수.
 * JSX / handleExecute / selectedIds state 는 도메인에 유지 — 도메인 의존이 강함.
 */
export const useEligibleItemsPreview = <TShift, TRes>(
  args: UseEligibleItemsPreviewArgs<TShift, TRes>,
): UseEligibleItemsPreviewResult<TRes> => {
  const [previewResult, setPreviewResult] = useState<TRes | null>(null);
  const [currentSource, setCurrentSource] =
    useState<EligibleItemsSource>('tableSelection');

  // 호출처가 매 렌더마다 인라인 객체 (`{ buildShift, onSubmit, ... }`) 로 args 를
  // 전달하면 deps 에 args 를 그대로 넣을 경우 `runPreview` 가 매 렌더 재생성되어
  // useCallback 효과가 무력화된다. 최신 args 를 ref 로 추적하고 `runPreview` 의
  // deps 는 `[]` 로 고정 — 외부로 노출되는 함수의 referential equality 보장.
  const argsRef = useRef(args);
  argsRef.current = args;

  const runPreview = useCallback(
    async (selectedIds: number[], idsOverride?: number[]): Promise<boolean> => {
      const targetIds = idsOverride ?? selectedIds;
      if (targetIds.length === 0) {
        message.warning('최소 1건 이상 선택해주세요.');
        return false;
      }

      const shiftResult = argsRef.current.buildShift();
      if ('error' in shiftResult) {
        message.warning(shiftResult.error);
        return false;
      }

      try {
        const result = await argsRef.current.onSubmit({
          ids: targetIds,
          shift: shiftResult.shift,
        });
        setPreviewResult(result);
        // 이력 화면 구분을 위해 선정 경로 기록.
        setCurrentSource(idsOverride ? 'excelUpload' : 'tableSelection');
        // 도메인별 후처리 (예: 기준일 변경의 dueDateOverrides 초기화).
        argsRef.current.onAfterPreviewSuccess?.();
        return true;
      } catch (e) {
        message.error(
          `Preview 실패: ${e instanceof Error ? e.message : String(e)}`,
        );
        return false;
      }
    },
    [],
  );

  return {
    previewResult,
    setPreviewResult,
    currentSource,
    runPreview,
  };
};
